import { User } from '~/services/types';

// Type declaration for Pendo global object
declare global {
  interface Window {
    pendo?: {
      initialize: (config: any) => void;
      identify: (visitor?: any, account?: any) => void;
      updateOptions: (options: any) => void;
      pageLoad: () => void;
      track: (eventName: string, properties?: any) => void;
    };
  }
}

const checkPendo = (pendo: any): pendo is NonNullable<typeof window.pendo> => {
  return typeof pendo === 'object' && pendo !== null && typeof pendo.initialize === 'function';
};

class PendoService {
  _isInitialized = false;

  get isInitialized() {
    return this._isInitialized;
  }

  set isInitialized(value: boolean) {
    this._isInitialized = value;
  }

  initialize = () => {
    if (this.isInitialized) return;

    if (checkPendo(window.pendo)) {
      // Initialize Pendo with basic configuration
      window.pendo.initialize({
        visitor: {
          id: undefined, // Will be set when user is identified
        },
        account: {
          id: undefined, // Will be set when user is identified
        },
      });
      this.isInitialized = true;
    }
  };

  identify = (user: User) => {
    if (checkPendo(window.pendo)) {
      if (!this.isInitialized) {
        this.initialize();
      }

      // Get the primary location for account data
      const primaryLocation = user.locations?.[0];
      
      // Identify the visitor (user) and account (gym location)
      window.pendo.identify({
        visitor: {
          id: user.id.toString(),
          email: user.email,
          firstName: user.user_profile?.first_name,
          lastName: user.user_profile?.last_name,
          role: user.role?.name,
          roleId: user.role?.id,
          phone: user.user_profile?.phone_number,
          startDate: user.user_profile?.start_date,
       },
       account: {
           id: primaryLocation?.location_id?.toString(),
           accountId: primaryLocation?.location_id?.toString(),
           name: primaryLocation?.name,
           type: primaryLocation?.type?.name,
           area_sft: primaryLocation?.area_sft,
           address: primaryLocation?.address_1,
           cs_phone: primaryLocation?.cs_phone,
           cs_email: primaryLocation?.cs_email,
           kiosk_enabled: primaryLocation?.kiosk_enabled,
           accountname: primaryLocation?.name,
       }
    });
    }
  };

  track = (eventName: string, properties?: any) => {
    if (checkPendo(window.pendo) && this.isInitialized) {
      window.pendo.track(eventName, properties);
    }
  };

  pageLoad = () => {
    if (checkPendo(window.pendo) && this.isInitialized) {
      window.pendo.pageLoad();
    }
  };

  updateOptions = (options: any) => {
    if (checkPendo(window.pendo) && this.isInitialized) {
      window.pendo.updateOptions(options);
    }
  };
}

export default new PendoService(); 